/**
 * @file src/services/repository/userRepository.js
 * @description Data access layer for user profile operations
 * 
 * Abstraction layer over Firebase Firestore for user profile and settings.
 * Stores non-authentication data like name, phone, 2FA status, subscription.
 * 
 * Key Patterns:
 * - Firestore doc path: users/{userId}
 * - Contains: displayName, phone, mfaEnabled, subscription status, preferences
 * - Auth state (email, password) stored in Firebase Auth, not Firestore
 * 
 * @dependencies
 *   - Firebase Firestore (read/write user documents)
 * 
 * @exports
 *   - getUserProfile(userId) - Get user's profile data
 *   - createUserProfile(userId, profileData) - Initialize new user
 *   - updateUserProfile(userId, updates) - Update profile fields
 *   - setUserPhoneNumber(userId, phone) - Set phone for 2FA
 *   - setUserSubscription(userId, planId) - Update subscription
 *   - getUserPreferences(userId) - Get user settings
 * 
 * @example
 *   const profile = await getUserProfile(user.uid);
 *   await updateUserProfile(user.uid, { displayName: 'John' });
 */

// src/services/repository/userRepository.js
// Database-agnostic user repository.
// Abstraction layer for user CRUD operations.
// Implementation: Firebase Firestore

import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import db from "../../firebase/db";

/**
 * Get user profile data
 * @param {string} userId - User UID
 * @returns {Promise<Object>} User data or empty object
 */
export const getUserProfile = async (userId) => {
    try {
        const snap = await getDoc(doc(db, "users", userId));
        return snap.exists() ? snap.data() : {};
    } catch (error) {
        console.error("getUserProfile error:", error);
        throw error;
    }
};

/**
 * Create or initialize user profile
 * @param {string} userId - User UID
 * @param {Object} data - User data to set
 */
export const initializeUserProfile = async (userId, data) => {
    try {
        await setDoc(doc(db, "users", userId), data, { merge: true });
    } catch (error) {
        console.error("initializeUserProfile error:", error);
        throw error;
    }
};

/**
 * Update user profile
 * @param {string} userId - User UID
 * @param {Object} updates - Fields to update
 */
export const updateUserProfile = async (userId, updates) => {
    try {
        await updateDoc(doc(db, "users", userId), updates);
    } catch (error) {
        console.error("updateUserProfile error:", error);
        throw error;
    }
};

/**
 * Get user plan and limits
 * @param {string} userId - User UID
 * @returns {Promise<Object>} Plan info { plan, capsuleLimit }
 */
export const getUserPlan = async (userId) => {
    try {
        const data = await getUserProfile(userId);
        return {
            plan: data.plan || (data.isPremium ? "legacy" : "free"),
            capsuleLimit: data.capsuleLimit,
        };
    } catch (error) {
        console.error("getUserPlan error:", error);
        throw error;
    }
};
